import { z } from 'zod';
import { idSchema } from './common.js';
// -----------------------------------------------------------------------------
// Post media limits (Session 11a)
// -----------------------------------------------------------------------------
/** Per-file upload cap for post media. 10 MiB matches the POD ceiling. */
export const POST_MEDIA_MAX_BYTES_PER_FILE = 10 * 1024 * 1024;
/** Maximum number of media items on a single post. */
export const POST_MEDIA_MAX_COUNT = 4;
/** Whitelist of accepted image mime types for post media uploads. */
export const POST_MEDIA_ALLOWED_MIME_TYPES = [
    'image/jpeg',
    'image/png',
    'image/heic',
    'image/webp',
];
// -----------------------------------------------------------------------------
// Posts
// -----------------------------------------------------------------------------
const postVisibilitySchema = z.enum([
    'public',
    'drivers',
    'followers',
    'fleet',
    'group',
]);
/**
 * Media attached to a post. Client uploads the binary directly to R2 via the
 * presigned-URL flow (POST /v1/social/uploads/presign) and POSTs the
 * resulting r2Key here along with metadata. postsService.create validates that
 * the r2Key sits under the caller's own prefix before persisting.
 */
const postMediaInputSchema = z.object({
    r2Key: z.string().trim().min(1).max(512),
    mimeType: z.string().trim().min(1).max(64),
    widthPx: z.number().int().positive().optional(),
    heightPx: z.number().int().positive().optional(),
    sizeBytes: z.number().int().positive(),
    altText: z.string().trim().max(2000).optional(),
});
export const createPostBodySchema = z
    .object({
    body: z.string().trim().min(1).max(4000),
    /** Omit for global feed; provide to scope into a group. */
    groupId: idSchema.optional(),
    /**
     * For ungrouped posts. Defaults to public — ungrouped posts can also be
     * 'drivers', 'followers', or 'fleet'. The 'group' value is only valid
     * when groupId is set; the service enforces this.
     */
    visibility: postVisibilitySchema.optional(),
    region: z.string().trim().max(40).optional(),
    media: z.array(postMediaInputSchema).max(POST_MEDIA_MAX_COUNT).optional(),
})
    .refine((data) => 
// visibility 'group' requires groupId; others require no groupId
(data.visibility === 'group' && data.groupId !== undefined) ||
    (data.visibility !== 'group' && data.groupId === undefined) ||
    // groupId without visibility is fine — service stamps 'group'
    (data.groupId !== undefined && data.visibility === undefined), {
    message: "Visibility 'group' must accompany a groupId; other visibilities must not.",
    path: ['visibility'],
});
export const updatePostBodySchema = z.object({
    body: z.string().trim().min(1).max(4000),
});
export const postIdParamSchema = z.object({ postId: idSchema });
export const listFeedQuerySchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
});
export const listPostsByRegionQuerySchema = z.object({
    region: z.string().trim().min(1).max(40),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
});
// -----------------------------------------------------------------------------
// Uploads (Session 11a)
// -----------------------------------------------------------------------------
/**
 * Request body for POST /v1/social/uploads/presign. Caller declares mime
 * + size up-front; the response contains a presigned PUT URL bound to a
 * freshly-minted R2 key under the caller's per-user prefix.
 */
export const postUploadPresignBodySchema = z.object({
    mimeType: z.enum(POST_MEDIA_ALLOWED_MIME_TYPES),
    sizeBytes: z
        .number()
        .int()
        .positive()
        .max(POST_MEDIA_MAX_BYTES_PER_FILE),
    /**
     * Where the upload is destined: feed post media (default) or chat message
     * media. Determines the R2 key prefix so retention policy can treat the
     * two differently later. Same bucket, same caps either way.
     */
    scope: z.enum(['post', 'message', 'avatar', 'bay']).default('post'),
});
// -----------------------------------------------------------------------------
// Comments
// -----------------------------------------------------------------------------
export const createCommentBodySchema = z.object({
    body: z.string().trim().min(1).max(2000),
});
export const updateCommentBodySchema = createCommentBodySchema;
export const commentIdParamSchema = z.object({ commentId: idSchema });
// -----------------------------------------------------------------------------
// Follows / Blocks / Mutes
// -----------------------------------------------------------------------------
export const userIdParamSchema = z.object({ userId: idSchema });
// -----------------------------------------------------------------------------
// Reports
// -----------------------------------------------------------------------------
export const createReportBodySchema = z.object({
    targetType: z.enum(['post', 'comment', 'user', 'group']),
    targetId: idSchema,
    reason: z.string().trim().min(2).max(80),
    details: z.string().trim().max(2000).optional(),
});
export const reviewReportBodySchema = z.object({
    status: z.enum(['dismissed', 'actioned', 'duplicate']),
    reviewNotes: z.string().trim().max(2000).optional(),
});
export const reportIdParamSchema = z.object({ reportId: idSchema });
// -----------------------------------------------------------------------------
// User search (compose a DM)
// -----------------------------------------------------------------------------
/**
 * Query for GET /v1/social/users/search. `q` matches handle or displayName
 * (ILIKE, server-side). Offset-paginated to match the customers/loads
 * list convention.
 */
export const userSearchQuerySchema = z.object({
    q: z.string().trim().min(2).max(40),
    limit: z.coerce.number().int().min(1).max(20).default(10),
    offset: z.coerce.number().int().min(0).default(0),
});
//# sourceMappingURL=social.js.map